import React, { useState, useRef, useEffect } from "react";
import { useGetUsersQuery, useAddUserMutation, useModifyUserMutation, useDeleteUserMutation } from "../../services/usersApi";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Card } from "primereact/card";
import { Toolbar } from "primereact/toolbar";
import { Toast } from "primereact/toast";
import { Dropdown } from "primereact/dropdown";
import { ColorPicker } from "primereact/colorpicker";
import BranchDropdown, { BranchView } from "../BranchDropdown";

export default function UserManagementPage() {
  const { data: users, isLoading, isFetching } = useGetUsersQuery();
  const [addUser] = useAddUserMutation();
  const [modifyUser] = useModifyUserMutation();
  const [deleteUser] = useDeleteUserMutation();
  const toast = useRef(null);

  const [newUser, setNewUser] = useState({ username: "", name: "", email: "", password: "", type: "" });
  const [showDialog, setShowDialog] = useState(false);
  const [editingPasswords, setEditingPasswords] = useState({});
  const [visibleRows, setVisibleRows] = useState(10);
  const tableWrapperRef = useRef(null);

  useEffect(() => {
    const calculateVisibleRows = () => {
      if (!tableWrapperRef.current) {
        return;
      }

      const tableTop = tableWrapperRef.current.getBoundingClientRect().top;
      const viewportHeight = window.innerHeight;
      const bottomSpacing = 32;
      const paginatorHeight = 56;
      const tableHeaderHeight = 48;
      const estimatedRowHeight = 46;
      const rowReductionFactor = 0.7;

      const availableHeight = viewportHeight - tableTop - bottomSpacing - paginatorHeight - tableHeaderHeight;
      const nextRows = Math.max(3, Math.floor((availableHeight / estimatedRowHeight) * rowReductionFactor));

      setVisibleRows((prevRows) => (prevRows === nextRows ? prevRows : nextRows));
    };

    calculateVisibleRows();
    window.addEventListener("resize", calculateVisibleRows);

    return () => {
      window.removeEventListener("resize", calculateVisibleRows);
    };
  }, []);

  const handleAddUser = async () => {
    try {
      // Ensure color is without # prefix for storage
      const userToAdd = {
        ...newUser,
        password: 'test',
        color: newUser.color && newUser.color.startsWith('#') ? newUser.color.substring(1) : newUser.color
      };
      await addUser(userToAdd);
      setNewUser({ name: "", email: "", type: "" });
      setShowDialog(false);
      toast.current.show({ severity: 'success', summary: 'Sukces', detail: 'Użytkownik dodany' });
    } catch (error) {
      toast.current.show({ severity: 'error', summary: 'Błąd', detail: 'Nie udało się dodać użytkownika' });
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await deleteUser(userId);
      toast.current.show({ severity: 'warn', summary: 'Usunięto', detail: 'Użytkownik został usunięty' });
    } catch (error) {
      toast.current.show({ severity: 'error', summary: 'Błąd', detail: 'Nie udało się usunąć użytkownika' });
    }
  };

  const handleRowEditComplete = async (e) => {
    const { newData } = e;
    try {
      // Ensure color is without # prefix for storage
      const userToUpdate = {
        ...newData,
        color: newData.color && newData.color.startsWith('#') ? newData.color.substring(1) : newData.color
      };
      
      // Check if password was actually edited (exists in editingPasswords and has a value)
      const passwordWasEdited = editingPasswords[newData._id];
      if (!passwordWasEdited || passwordWasEdited.trim() === '') {
        // Password was not changed, remove it from update
        delete userToUpdate.password;
      } else {
        // Use the edited password from state
        userToUpdate.password = passwordWasEdited;
      }
      
      // Clear the editing password state for this user
      setEditingPasswords(prev => {
        const updated = { ...prev };
        delete updated[newData._id];
        return updated;
      });
      
      await modifyUser(userToUpdate);
      toast.current.show({ severity: 'success', summary: 'Zapisano', detail: 'Użytkownik zaktualizowany' });
    } catch (error) {
      toast.current.show({ severity: 'error', summary: 'Błąd', detail: 'Nie udało się zaktualizować użytkownika' });
    }
  };

  const textEditor = (options) => (
    <InputText type="text" value={options.value} onChange={(e) => options.editorCallback(e.target.value)} />
  );

  const handleRowEditCancel = (e) => {
    // Clear editing password state when edit is cancelled
    const userId = e.data._id;
    setEditingPasswords(prev => {
      const updated = { ...prev };
      delete updated[userId];
      return updated;
    });
  };

  const passwordEditor = (options) => {
    // Get the user ID from the row data
    const userId = options.rowData._id;
    
    return (
      <InputText
        type="password"
        value={editingPasswords[userId] || ''}
        placeholder="Nowe hasło"
        onChange={(e) => {
          const newValue = e.target.value;
          setEditingPasswords(prev => ({
            ...prev,
            [userId]: newValue
          }));
          // Don't call editorCallback to avoid updating the actual row data
        }}
      />
    );
  };

  const passwordBody = (rowData) => {
    return rowData.password ? '******' : '';
  };

  const branchEditor = (options) => (
    <BranchDropdown
      selectedBranch={options.value}
      onBranchSelect={(e) => options.editorCallback(e)}
      defaultValue={'null'}
      defaultLabel={'Brak branży'}
      className="w-full"
      floating={false}
      label={false}
    />
  );

  const branchView = (options) => (
    <BranchView
      value={options.branch}
      defaultLabel="Brak"
      defaultValue=""
    />
  );

  const colorEditor = (options) => {
    const colorValue = options.value ? (options.value.startsWith('#') ? options.value : `#${options.value}`) : '#ffffff';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <ColorPicker
          value={options.value}
          onChange={(e) => options.editorCallback(e.value)}
          format="hex"
          appendTo={document.body}
        />
        <div
          style={{ 
            width: '32px', 
            height: '32px', 
            borderRadius: '50%', 
            border: '1px solid #ccc',
            backgroundColor: colorValue
          }}
        ></div>
      </div>
    );
  };

  const colorBody = (rowData) => {
    const colorValue = rowData.color ? (rowData.color.startsWith('#') ? rowData.color : `#${rowData.color}`) : '#ffffff';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div
          style={{ 
            width: '24px', 
            height: '24px', 
            borderRadius: '50%', 
            border: '1px solid #ccc',
            backgroundColor: colorValue,
            flexShrink: 0
          }}
        ></div>
        <span>{rowData.color || 'Brak'}</span>
      </div>
    );
  };

  const actionBodyTemplate = (rowData) => (
    <Button
      label="Usuń"
      icon="pi pi-trash"
      className="p-button-danger p-button-sm"
      onClick={() => handleDeleteUser(rowData._id)}
    />
  );

  const leftToolbarTemplate = () => (
    <Button
      label="Dodaj użytkownika"
      icon="pi pi-plus"
      className="p-button-success"
      onClick={() => setShowDialog(true)}
    />
  );

  const userTypes =
    [
      { label: "Pracownik", value: "user" },
      { label: "Koordynator", value: "manager" },
      { label: "Administrator", value: "admin" }
    ]

  return (
    <div className="p-4">
      <Toast ref={toast} />

      <Card title="Zarządzanie użytkownikami">
        <Toolbar className="mb-4" left={leftToolbarTemplate} />

        <div ref={tableWrapperRef}>
          <DataTable
            value={users}
            loading={isLoading || isFetching}
            paginator
            rows={visibleRows}
            editMode="row"
            dataKey="_id"
            onRowEditComplete={handleRowEditComplete}
            onRowEditCancel={handleRowEditCancel}
          >
            <Column field="username" header="Login" editor={textEditor} sortable></Column>
            <Column field="name" header="Nazwa" editor={textEditor} sortable></Column>
            <Column field="email" header="Email" editor={textEditor} sortable></Column>
            <Column field="color" header="Kolor" editor={colorEditor} body={colorBody} sortable></Column>
            <Column field="branch" header="Branża" editor={branchEditor} body={(row) => (
              <BranchView
                value={row.branch}
                defaultLabel="Brak"
                defaultValue=""
              />
            )} sortable></Column>
            <Column
              field="type"
              header="Typ"
              editor={(options) => (
                <Dropdown
                  value={options.value}
                  onChange={(e) => options.editorCallback(e.target.value)}
                  options={userTypes}
                  placeholder="Wybierz typ..."
                />
              )}
              body={(rowData) => userTypes.find(opt => opt.value === rowData.type)?.label}
              sortable
            />
            <Column field="password" header="Hasło" body={passwordBody} editor={passwordEditor}></Column>
            <Column rowEditor headerStyle={{ width: '7rem' }} bodyStyle={{ textAlign: 'center' }}></Column>
            <Column body={actionBodyTemplate} header="Akcje"></Column>
          </DataTable>
        </div>
      </Card>

      <Dialog header="Dodaj użytkownika" visible={showDialog} style={{ width: '30vw' }} onHide={() => setShowDialog(false)}>
        <div className="p-fluid">
          <div className="field">
            <label htmlFor="username">Login</label>
            <InputText id="username" value={newUser.username} onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="name">Nazwa</label>
            <InputText id="name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="email">Email</label>
            <InputText id="email" type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="color">Kolor awatara</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <ColorPicker
                id="color"
                value={newUser.color}
                onChange={(e) => setNewUser({ ...newUser, color: e.value })}
                format="hex"
                appendTo={document.body}
              />
              <div
                style={{ 
                  width: '32px', 
                  height: '32px', 
                  borderRadius: '50%', 
                  border: '1px solid #ccc',
                  backgroundColor: newUser.color ? (newUser.color.startsWith('#') ? newUser.color : `#${newUser.color}`) : '#ffffff',
                  flexShrink: 0
                }}
              ></div>
            </div>
          </div>
          <div className="field">
            <BranchDropdown
              selectedBranch={newUser.branch}
              onBranchSelect={(e) => setNewUser({ ...newUser, branch: e })}
              defaultValue={'null'}
              defaultLabel={'Brak branży'}
              className="w-full"
              floating={false}
            />
          </div>
          <div className="field">
            <label htmlFor="type">Typ</label>
            <Dropdown
              value={newUser.type}
              onChange={(e) => setNewUser({ ...newUser, type: e.target.value })}
              options={userTypes}
              placeholder="Wybierz typ..."
            />          </div>
          <div className="field">
            <label htmlFor="password">Hasło</label>
            <Password id="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} toggleMask feedback={false} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
            <Button label="Anuluj" icon="pi pi-times" className="p-button-text" onClick={() => setShowDialog(false)} />
            <Button label="Zapisz" icon="pi pi-check" onClick={handleAddUser} autoFocus />
          </div>
        </div>
      </Dialog>
    </div>
  );
}
